import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Map countries to languages
const countryToLanguage = {
  // Americas → English
  'US': 'en', 'CA': 'en', 'GB': 'en', 'AU': 'en', 'NZ': 'en',
  'IE': 'en', 'ZA': 'en',
  
  // China and Chinese-speaking → Chinese
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'MO': 'zh', 'SG': 'zh',
  
  // French-speaking → French
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'LU': 'fr', 'MC': 'fr',
  'SN': 'fr', 'CI': 'fr', 'ML': 'fr', 'BF': 'fr', 'NE': 'fr',
  'TG': 'fr', 'BJ': 'fr', 'GA': 'fr', 'CG': 'fr', 'CD': 'fr',
  'CM': 'fr', 'TD': 'fr', 'CF': 'fr', 'DJ': 'fr', 'KM': 'fr',
  'MG': 'fr', 'MU': 'fr', 'RE': 'fr', 'YT': 'fr', 'NC': 'fr',
  'PF': 'fr', 'WF': 'fr', 'MQ': 'fr', 'GP': 'fr', 'GF': 'fr',
  'HT': 'fr',
  
  // German-speaking → German
  'DE': 'de', 'AT': 'de', 'LI': 'de',
  
  // Spanish-speaking → Spanish
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es',
  'VE': 'es', 'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es',
  'BO': 'es', 'DO': 'es', 'HN': 'es', 'SV': 'es', 'NI': 'es',
  'CR': 'es', 'PA': 'es', 'UY': 'es', 'PY': 'es', 'PR': 'es',
  
  // Italian-speaking → Italian
  'IT': 'it', 'SM': 'it', 'VA': 'it',
  
  // Portuguese-speaking → Portuguese
  'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt', 'CV': 'pt',
  'GW': 'pt', 'ST': 'pt', 'TL': 'pt',
  
  // Dutch-speaking → Dutch
  'NL': 'nl', 'SR': 'nl', 'AN': 'nl', 'AW': 'nl', 'CW': 'nl',
  
  // Russian-speaking → Russian
  'RU': 'ru', 'BY': 'ru', 'KZ': 'ru', 'KG': 'ru',
  
  // Japanese → Japanese
  'JP': 'ja',
  
  // Arabic-speaking → Arabic
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'MA': 'ar', 'DZ': 'ar',
  'TN': 'ar', 'LY': 'ar', 'SD': 'ar', 'IQ': 'ar', 'SY': 'ar',
  'JO': 'ar', 'LB': 'ar', 'KW': 'ar', 'QA': 'ar', 'BH': 'ar',
  'OM': 'ar', 'YE': 'ar',
  
  // Polish → Polish
  'PL': 'pl',
};

// Default language for unknown countries
const DEFAULT_LANGUAGE = 'en';

const AutoLanguageDetector = () => {
  const { i18n } = useTranslation();
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    // Check if user has already set a language preference
    const savedLanguage = localStorage.getItem('xperience_language');
    const hasVisitedBefore = localStorage.getItem('xperience_visited');
    
    if (savedLanguage) {
      // User has explicitly chosen a language, respect it
      if (i18n.language !== savedLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
      setDetected(true);
      return;
    }
    
    if (hasVisitedBefore) {
      // User has visited before but didn't change language, keep current
      setDetected(true);
      return;
    }

    // First visit - detect country and set language
    const detectAndSetLanguage = async () => {
      try {
        // Use HTTPS-compatible geolocation service
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        if (data.country_code) {
          const detectedLanguage = countryToLanguage[data.country_code] || DEFAULT_LANGUAGE;
          
          // Check if we support this language
          const supportedLanguages = ['fr', 'en', 'de', 'es', 'it', 'pt', 'nl', 'ru', 'ja', 'zh', 'pl', 'ar'];
          const languageToUse = supportedLanguages.includes(detectedLanguage) ? detectedLanguage : DEFAULT_LANGUAGE;
          
          console.log(`XPerience: Auto-detected country: ${data.country_code} → Language: ${languageToUse}`);
          
          i18n.changeLanguage(languageToUse);
          localStorage.setItem('xperience_auto_language', languageToUse);
        }
      } catch (error) {
        console.log('XPerience: Could not detect location, using default language');
      } finally {
        localStorage.setItem('xperience_visited', 'true');
        setDetected(true);
      }
    };

    detectAndSetLanguage();
  }, [i18n]);

  return null; // This component doesn't render anything
};

// Hook to manually change language and save preference
export const useLanguageSwitch = () => {
  const { i18n } = useTranslation();
  
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('xperience_language', langCode);
  };
  
  return { changeLanguage, currentLanguage: i18n.language };
};

export default AutoLanguageDetector;
